import axios from 'axios';
import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { IReceiptPort } from '@onboarding/application/ports';
import type { ReceiptDto } from '@onboarding/application/dtos';
import { createReceiptHeader, createBrandFiscalChoice } from '@onboarding/domain/value-objects';

const onbLog = logger.forDomain('ONB');
const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Use case for persisting receipt headers during onboarding.
 *
 * Flow: ReceiptDto → domain value objects → port (one header per brand).
 *
 * @param props.receiptPort - IReceiptPort implementation (injected)
 * @returns Use case executor
 */
export const saveReceiptHeaderUseCase = (props: { receiptPort: IReceiptPort }) => {
  const { receiptPort } = props;

  const execute = async (dto: ReceiptDto): Promise<void> => {
    const span = tracer.startSpan('ONB.save_receipt_headers');
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const defaultHeader = createReceiptHeader({
          businessName: dto.businessName,
          legalName: dto.legalName,
          rfc: dto.rfc,
          fiscalAddress: dto.fiscalAddress,
        });

        const brandChoices = dto.brandChoices.map((choice) =>
          createBrandFiscalChoice({
            brandUuid: choice.brandUuid,
            brandName: choice.brandName,
            useRegisteredData: choice.useRegisteredData,
            customLegalName: choice.customLegalName,
            customRfc: choice.customRfc,
            customFiscalAddress: choice.customFiscalAddress,
          }),
        );

        span.setAttribute('onb.brand_count', brandChoices.length);

        await receiptPort.saveReceiptHeaders(defaultHeader, brandChoices);

        onbLog.info('receipt_header_saved', {
          'onb.brand_count': brandChoices.length,
        });
      } catch (error) {
        onbLog.error('receipt_header_save_failed', {
          'error.kind': (error as { constructor?: { name?: string } })?.constructor?.name ?? 'UnknownError',
          'error.message': error instanceof Error ? error.message : String(error),
          'http.status_code': axios.isAxiosError(error) ? error.response?.status : undefined,
          'onb.brand_count': dto.brandChoices.length,
        });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
